<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import ActionCard from 'dashboard/components-next/aivoice/ActionCard.vue';
import CreateActionDialog from 'dashboard/components-next/aivoice/CreateActionDialog.vue';
import DeleteDialog from 'dashboard/components-next/aivoice/DeleteDialog.vue';
import VoiceAgentSelector from 'dashboard/components-next/aivoice/VoiceAgentSelector.vue';
import ActionPageEmptyState from 'dashboard/routes/dashboard/aivoice/emptyStates/ActionPageEmptyState.vue';

const store = useStore();

const uiFlags = useMapGetter('voiceAgentActions/getUIFlags');
const actions = useMapGetter('voiceAgentActions/getRecords');
const voiceAgents = useMapGetter('voiceAgents/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const actionsMeta = useMapGetter('voiceAgentActions/getMeta');
const selectedVoiceAgent = ref('all');

const selectedAction = ref(null);
const deleteActionDialog = ref(null);

const handleDelete = () => {
  deleteActionDialog.value.dialogRef.open();
};

const showCreateDialog = ref(false);
const createActionDialog = ref(null);

const shouldShowVoiceAgentSelector = computed(() => {
  if (!voiceAgents.value || voiceAgents.value.length === 0) return false;
  return !isFetching.value;
});

const handleCreateAction = () => {
  selectedAction.value = null;
  showCreateDialog.value = true;
  nextTick(() => createActionDialog.value.dialogRef.open());
};

const handleEditAction = () => {
  showCreateDialog.value = true;
  nextTick(() => createActionDialog.value.dialogRef.open());
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
  selectedAction.value = null;
};

const handleAction = ({ action, id }) => {
  selectedAction.value = actions.value.find(a => id === a.id);

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'edit') {
      handleEditAction();
    }
  });
};

const fetchActions = (page = 1) => {
  const filterParams = { page };

  if (selectedVoiceAgent.value !== 'all') {
    filterParams.voiceAgentId = selectedVoiceAgent.value;
  }
  store.dispatch('voiceAgentActions/get', filterParams);
};

const handleVoiceAgentFilterChange = voiceAgent => {
  selectedVoiceAgent.value = voiceAgent;
  fetchActions();
};

const onPageChange = page => fetchActions(page);

const onDeleteSuccess = () => {
  if (actions.value?.length === 0 && actionsMeta.value?.page > 1) {
    onPageChange(actionsMeta.value.page - 1);
  }
};

onMounted(() => {
  if (!voiceAgents.value || !voiceAgents.value.length) {
    store.dispatch('voiceAgents/get');
  }
  fetchActions();
});
</script>

<template>
  <PageLayout
    :header-title="$t('AI_VOICE.ACTIONS.HEADER')"
    :button-label="$t('AI_VOICE.ACTIONS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="actionsMeta.totalCount"
    :current-page="actionsMeta.page"
    :show-pagination-footer="!isFetching && !!actions.length"
    :is-fetching="isFetching"
    :is-empty="!actions.length"
    @update:current-page="onPageChange"
    @click="handleCreateAction"
  >
    <template #emptyState>
      <ActionPageEmptyState @click="handleCreateAction" />
    </template>

    <template #controls>
      <div v-if="shouldShowVoiceAgentSelector" class="mb-4 -mt-3 flex gap-3">
        <VoiceAgentSelector
          :voice-agent-id="selectedVoiceAgent"
          @update="handleVoiceAgentFilterChange"
        />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <ActionCard
          v-for="action in actions"
          :id="action.id"
          :key="action.id"
          :name="action.name"
          :title="action.title"
          :description="action.description"
          :icon="action.icon"
          :api-spec="action.api_spec"
          :enabled="action.enabled"
          :voice-agents="action.voice_agents"
          :created-at="action.created_at"
          @action="handleAction"
        />
      </div>
    </template>

    <CreateActionDialog
      v-if="showCreateDialog"
      ref="createActionDialog"
      :action="selectedAction"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedAction"
      ref="deleteActionDialog"
      :entity="selectedAction"
      type="VoiceAgentActions"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
